/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors'
import { TicketSheet } from '../../../shared/ticket/TicketSheet'
import { TicketButton } from '../../../shared/ticket/TicketButton';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import type { EmployeeRecord } from '../../../team/store/useEmployeesStore';

type AttributionPickerProps = {
  visible: boolean;
  lineId: string | null;
  itemName: string;
  currentPerformedByUserId: string | null;
  currentSoldByUserId: string | null;
  onClose: () => void;
  onApply: (lineId: string, performedByUserId: string | null, soldByUserId: string | null) => void;
};

export function PosCartLineAttributionPicker({
  visible,
  lineId,
  itemName,
  currentPerformedByUserId,
  currentSoldByUserId,
  onClose,
  onApply,
}: AttributionPickerProps) {
  const employees = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const [performedBy, setPerformedBy] = useState<string | null>(null);
  const [soldBy, setSoldBy] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (visible) {
      setPerformedBy(currentPerformedByUserId);
      setSoldBy(currentSoldByUserId);
      setSearch('');
      void loadEmployees();
    }
  }, [visible, currentPerformedByUserId, currentSoldByUserId, loadEmployees]);

  const activeEmployees = useMemo(
    () => withSelfOwner(employees, selfOwner).filter((e) => e.isActive !== false),
    [employees, selfOwner]
  );

  const filteredEmployees = useMemo(() => {
    if (!search.trim()) return activeEmployees;
    const q = search.trim().toLowerCase();
    return activeEmployees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.email ?? '').toLowerCase().includes(q)
    );
  }, [activeEmployees, search]);

  const employeeName = useCallback(
    (userId: string | null): string => {
      if (!userId) return 'Sin asignar';
      const emp = activeEmployees.find((e) => e.authUserId === userId);
      return emp?.name ?? 'Desconocido';
    },
    [activeEmployees]
  );

  const handleApply = useCallback(() => {
    if (lineId) {
      onApply(lineId, performedBy, soldBy);
    }
    onClose();
  }, [lineId, performedBy, soldBy, onApply, onClose]);

  const handleClear = useCallback(() => {
    if (lineId) {
      onApply(lineId, null, null);
    }
    onClose();
  }, [lineId, onApply, onClose]);

  return (
    <TicketSheet
      visible={visible}
      title="Asignar comisión"
      subtitle={itemName}
      onClose={onClose}
      fitContent
      dialogStyle={styles.dialog}
      footer={
        <View style={styles.actions}>
          <TicketButton
            label="Limpiar"
            variant="ghost"
            onPress={handleClear}
            style={{ flex: 1 }}
          />
          <TicketButton
            label="Aplicar"
            variant="gold"
            onPress={handleApply}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      {/* Compact selection summary — single line */}
      <View style={styles.selectionBar}>
        <Ionicons name="person-circle" size={16} color={ticketColors.ink3} />
        <Text style={styles.selectionText} numberOfLines={1}>
          <Text style={styles.selectionLabel}>Realiza </Text>
          <Text style={styles.selectionValue}>{employeeName(performedBy)}</Text>
          <Text style={styles.selectionDot}> · </Text>
          <Text style={styles.selectionLabel}>Vende </Text>
          <Text style={styles.selectionValue}>{employeeName(soldBy)}</Text>
        </Text>
      </View>

      {/* Search input with icon */}
      <View style={styles.searchWrap}>
        <Ionicons name="search-outline" size={16} color={ticketColors.ink3} style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder="Buscar empleado…"
          placeholderTextColor={ticketColors.ink3}
        />
        {search.length > 0 ? (
          <TouchableOpacity
            onPress={() => setSearch('')}
            style={styles.searchClear}
            hitSlop={8}
            accessibilityLabel="Limpiar búsqueda"
          >
            <Ionicons name="close-circle" size={16} color={ticketColors.ink3} />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Employee list — compact rows with inline buttons */}
      <ScrollView
        style={styles.employeeList}
        contentContainerStyle={styles.employeeListContent}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
      >
        {filteredEmployees.length === 0 ? (
          <Text style={styles.empty}>No hay empleados.</Text>
        ) : (
          filteredEmployees.map((emp, idx) => (
            <CompactEmployeeRow
              key={emp.id}
              employee={emp}
              isPerformedBy={performedBy === emp.authUserId}
              isSoldBy={soldBy === emp.authUserId}
              isLast={idx === filteredEmployees.length - 1}
              onSetPerformedBy={() =>
                setPerformedBy((prev) =>
                  prev === emp.authUserId ? null : emp.authUserId ?? null
                )
              }
              onSetSoldBy={() =>
                setSoldBy((prev) =>
                  prev === emp.authUserId ? null : emp.authUserId ?? null
                )
              }
            />
          ))
        )}
      </ScrollView>
    </TicketSheet>
  );
}

function CompactEmployeeRow({
  employee,
  isPerformedBy,
  isSoldBy,
  isLast,
  onSetPerformedBy,
  onSetSoldBy,
}: {
  employee: EmployeeRecord;
  isPerformedBy: boolean;
  isSoldBy: boolean;
  isLast: boolean;
  onSetPerformedBy: () => void;
  onSetSoldBy: () => void;
}) {
  const initials = useMemo(() => {
    const parts = employee.name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }, [employee.name]);

  return (
    <View style={[styles.empRow, isLast && styles.empRowLast]}>
      {/* Left: avatar + name/role */}
      <View style={styles.empLeft}>
        <View style={styles.empAvatar}>
          <Text style={styles.empAvatarText}>{initials}</Text>
        </View>
        <View style={styles.empInfo}>
          <Text style={styles.empName} numberOfLines={1}>
            {employee.name}
          </Text>
          {employee.role ? (
            <Text style={styles.empRole} numberOfLines={1}>
              {employee.role}
            </Text>
          ) : null}
        </View>
      </View>

      {/* Right: inline role buttons */}
      <View style={styles.empButtons}>
        <TouchableOpacity
          style={[styles.roleBtn, isPerformedBy && styles.roleBtnActive]}
          onPress={onSetPerformedBy}
          accessibilityRole="button"
          accessibilityLabel={`Marcar a ${employee.name} como realiza`}
          accessibilityState={{ selected: isPerformedBy }}
        >
          <Text style={[styles.roleBtnText, isPerformedBy && styles.roleBtnTextActive]}>
            Realiza
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.roleBtn, isSoldBy && styles.roleBtnActive]}
          onPress={onSetSoldBy}
          accessibilityRole="button"
          accessibilityLabel={`Marcar a ${employee.name} como vende`}
          accessibilityState={{ selected: isSoldBy }}
        >
          <Text style={[styles.roleBtnText, isSoldBy && styles.roleBtnTextActive]}>
            Vende
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // ─── Desktop dialog ───
  dialog: {
    width: 440,
    maxWidth: '90%',
  },

  // ─── Selection bar ───
  selectionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
  },
  selectionText: {
    fontSize: 12,
    fontFamily: ticketFonts.sans,
    flex: 1,
  },
  selectionLabel: {
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  selectionValue: {
    fontWeight: '700',
    color: ticketColors.ink,
  },
  selectionDot: {
    color: ticketColors.ink3,
  },

  // ─── Search ───
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    minHeight: 38,
  },
  searchIcon: {
    paddingLeft: 10,
  },
  searchInput: {
    flex: 1,
    borderWidth: 0,
    backgroundColor: 'transparent',
    color: ticketColors.ink,
    minHeight: 38,
    fontSize: 13,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    paddingHorizontal: 8,
  },
  searchClear: {
    paddingHorizontal: 10,
  },

  // ─── Employee list ───
  employeeList: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    maxHeight: 320,
  },
  employeeListContent: {
    paddingBottom: spacing.xs,
  },
  empty: {
    textAlign: 'center',
    color: ticketColors.ink3,
    paddingVertical: 16,
    fontSize: 13,
    fontFamily: ticketFonts.sans,
  },

  // ─── Compact employee row ───
  empRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    gap: 8,
  },
  empRowLast: {
    borderBottomWidth: 0,
  },
  empLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  empAvatar: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  empAvatarText: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  empInfo: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  empName: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    lineHeight: 17,
  },
  empRole: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    lineHeight: 14,
  },

  // ─── Inline role buttons ───
  empButtons: {
    flexDirection: 'row',
    gap: 6,
    flexShrink: 0,
  },
  roleBtn: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  roleBtnActive: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorderSoft,
  },
  roleBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  roleBtnTextActive: {
    color: ticketColors.brandInk,
    fontWeight: '700',
  },

  // ─── Footer actions ───
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
});
